// Seeded branching route for one sector (pure, Phaser-free, deterministic per rng state).
//
// Shape (7 depths):  0 start → 1..4 choices in up to 3 lanes → 5 station → 6 boss.
// Edges only go to the next depth and never cross on the map (lanes are ordered left →
// right). Every node is reachable and every node except the boss has an outgoing edge.
// Content rules: combat dominates early; each sector offers at least one elite, one loot
// and one repair node; no edge joins two nodes of the same special type; every path from
// start to boss fights at least two combat/elite rooms; room templates do not repeat
// within a sector while unused templates of that type remain.

import type { Rng } from '../../core/rng';
import type { NodeType, RouteGraph, RouteNode } from '../../core/types';
import { roomsOfType } from '../../data/rooms';

export const ROUTE_DEPTHS = 7;
export const ROUTE_LANES = 3;
const CHOICE_DEPTHS = [1, 2, 3, 4] as const;
const STATION_DEPTH = 5;
const BOSS_DEPTH = 6;
const MAX_ATTEMPTS = 60;

type ChoiceType = 'combat' | 'elite' | 'loot' | 'repair';

/** Per-depth weights for choice nodes: combat early, specials later. */
const TYPE_WEIGHTS: Record<number, Record<ChoiceType, number>> = {
  1: { combat: 0.85, loot: 0.15, elite: 0, repair: 0 },
  2: { combat: 0.55, loot: 0.22, elite: 0.23, repair: 0 },
  3: { combat: 0.4, loot: 0.15, elite: 0.25, repair: 0.2 },
  4: { combat: 0.32, loot: 0.15, elite: 0.25, repair: 0.28 },
};
const CHOICE_TYPES: readonly ChoiceType[] = ['combat', 'elite', 'loot', 'repair'];

interface Draft {
  /** Lanes per depth (sorted). */
  lanes: number[][];
  /** Types per depth, parallel to lanes. */
  types: NodeType[][];
  /** Edges per depth d: pairs of indices into lanes[d] / lanes[d + 1]. */
  edges: [number, number][][];
}

const nodeId = (sector: number, depth: number, lane: number) => `s${sector}-d${depth}-l${lane}`;

/** Lanes for a choice depth: three lanes, or two spread out (sometimes adjacent). */
function rollLanes(rng: Rng, depth: number): number[] {
  const three = depth === 1 ? rng.chance(0.55) : rng.chance(0.62);
  if (three) return [0, 1, 2];
  const r = rng.next();
  if (r < 0.6) return [0, 2];
  return r < 0.8 ? [0, 1] : [1, 2];
}

/** Two edges cross when their endpoints are ordered differently on the two depths. */
function crosses(a: [number, number], b: [number, number], fromLanes: number[], toLanes: number[]): boolean {
  const a0 = fromLanes[a[0]], a1 = toLanes[a[1]];
  const b0 = fromLanes[b[0]], b1 = toLanes[b[1]];
  return (a0 < b0 && a1 > b1) || (a0 > b0 && a1 < b1);
}

/**
 * Non-crossing edges covering both sides: a staircase merge of the two sorted lane lists,
 * then a few extra short diagonals that keep the drawing planar (more route choice).
 */
function connect(rng: Rng, from: number[], to: number[]): [number, number][] {
  const edges: [number, number][] = [[0, 0]];
  let i = 0;
  let j = 0;
  while (i < from.length - 1 || j < to.length - 1) {
    if (i === from.length - 1) j++;
    else if (j === to.length - 1) i++;
    else {
      const nextFrom = from[i + 1];
      const nextTo = to[j + 1];
      if (nextFrom === nextTo) {
        i++;
        j++;
      } else if (nextFrom < nextTo) i++;
      else j++;
    }
    edges.push([i, j]);
  }
  // Optional diagonals between lanes at most one apart.
  for (let a = 0; a < from.length; a++) {
    for (let b = 0; b < to.length; b++) {
      if (edges.some((e) => e[0] === a && e[1] === b)) continue;
      if (Math.abs(from[a] - to[b]) > 1) continue;
      const cand: [number, number] = [a, b];
      if (edges.some((e) => crosses(e, cand, from, to))) continue;
      if (rng.chance(0.55)) edges.push(cand);
    }
  }
  return edges.sort((p, q) => p[0] - q[0] || p[1] - q[1]);
}

function rollTypes(rng: Rng, depth: number, count: number): ChoiceType[] {
  const w = TYPE_WEIGHTS[depth];
  const out: ChoiceType[] = [];
  for (let k = 0; k < count; k++) {
    const t = rng.weighted(CHOICE_TYPES, (c) => {
      // Never two of the same special type at one depth.
      if (c !== 'combat' && out.includes(c)) return 0;
      return w[c];
    });
    out.push(t ?? 'combat');
  }
  return out;
}

function buildDraft(rng: Rng): Draft {
  const lanes: number[][] = [[1]];
  const types: NodeType[][] = [['start']];
  for (const d of CHOICE_DEPTHS) {
    const l = rollLanes(rng, d);
    lanes.push(l);
    types.push(rollTypes(rng, d, l.length));
  }
  lanes.push([1], [1]);
  types.push(['station'], ['boss']);
  const edges: [number, number][][] = [];
  for (let d = 0; d < BOSS_DEPTH; d++) edges.push(connect(rng, lanes[d], lanes[d + 1]));
  return { lanes, types, edges };
}

/** Make sure the sector offers every required special type (converting combat nodes). */
function ensureRequired(rng: Rng, draft: Draft): void {
  const need: { type: ChoiceType; depths: number[] }[] = [
    { type: 'elite', depths: [3, 2, 4] },
    { type: 'loot', depths: [2, 3, 1, 4] },
    { type: 'repair', depths: [4, 3] },
  ];
  for (const { type, depths } of need) {
    if (draft.types.some((t) => t.includes(type))) continue;
    for (const d of depths) {
      const t = draft.types[d];
      if (t.includes(type)) break;
      const combatIdx = t.map((v, i) => (v === 'combat' ? i : -1)).filter((i) => i >= 0);
      // Keep at least one combat option at depth 1.
      if (combatIdx.length === 0 || (d === 1 && combatIdx.length < 2)) continue;
      t[rng.pick(combatIdx)] = type;
      break;
    }
  }
}

function isFight(t: NodeType): boolean {
  return t === 'combat' || t === 'elite' || t === 'start' || t === 'boss';
}

function validDraft(draft: Draft): boolean {
  const all = draft.types.flat();
  if (!all.includes('elite') || !all.includes('loot') || !all.includes('repair')) return false;
  // Combat dominant early: depth 1 has combat, depths 1–2 are at least half combat.
  if (!draft.types[1].includes('combat')) return false;
  const early = [...draft.types[1], ...draft.types[2]];
  if (early.filter((t) => t === 'combat').length * 2 < early.length) return false;
  // No edge joins two specials of the same type (no repair → repair).
  for (let d = 0; d < BOSS_DEPTH; d++) {
    for (const [a, b] of draft.edges[d]) {
      const ta = draft.types[d][a];
      const tb = draft.types[d + 1][b];
      if (ta === tb && ta !== 'combat') return false;
    }
  }
  // Every start → boss path fights at least two combat/elite rooms among the choices:
  // minimum fights over all paths via DP on the layered graph.
  let best = draft.types[0].map(() => 0);
  for (let d = 1; d <= BOSS_DEPTH; d++) {
    const cur = draft.types[d].map(() => Infinity);
    for (const [a, b] of draft.edges[d - 1]) {
      const t = draft.types[d][b];
      const gain = d >= 1 && d <= 4 && isFight(t) ? 1 : 0;
      cur[b] = Math.min(cur[b], best[a] + gain);
    }
    best = cur;
  }
  return best[0] >= 2;
}

/** Hand-made safe layout used if every seeded attempt failed validation (never expected). */
function fallbackDraft(): Draft {
  return {
    lanes: [[1], [0, 2], [0, 1, 2], [0, 2], [0, 1, 2], [1], [1]],
    types: [
      ['start'],
      ['combat', 'combat'],
      ['combat', 'loot', 'combat'],
      ['elite', 'combat'],
      ['repair', 'combat', 'elite'],
      ['station'],
      ['boss'],
    ],
    edges: [
      [[0, 0], [0, 1]],
      [[0, 0], [0, 1], [1, 1], [1, 2]],
      [[0, 0], [1, 0], [1, 1], [2, 1]],
      [[0, 0], [0, 1], [1, 1], [1, 2]],
      [[0, 0], [1, 0], [2, 0]],
      [[0, 0]],
    ],
  };
}

/** Assign templates of the node's type, avoiding repeats within the sector while possible. */
function assignRooms(rng: Rng, nodes: RouteNode[]): void {
  const pools = new Map<NodeType, string[]>();
  const used = new Map<string, number>();
  for (const n of nodes) {
    const all = roomsOfType(n.type).map((r) => r.id);
    if (all.length === 0) throw new Error(`no room templates of type ${n.type}`);
    let pool = pools.get(n.type);
    if (!pool || pool.length === 0) {
      // (Re)fill with a fresh shuffle; least-used first when templates must repeat.
      pool = rng.shuffle(all).sort((a, b) => (used.get(a) ?? 0) - (used.get(b) ?? 0));
      pools.set(n.type, pool);
    }
    const id = pool.shift()!;
    used.set(id, (used.get(id) ?? 0) + 1);
    n.roomId = id;
  }
}

/** Seeded branching route for one sector (pure function). */
export function generateRoute(sectorIndex: number, rng: Rng): RouteGraph {
  let draft: Draft | null = null;
  for (let attempt = 0; attempt < MAX_ATTEMPTS && !draft; attempt++) {
    const d = buildDraft(rng);
    ensureRequired(rng, d);
    if (validDraft(d)) draft = d;
  }
  draft ??= fallbackDraft();

  const nodes: Record<string, RouteNode> = {};
  const ordered: RouteNode[] = [];
  for (let depth = 0; depth < ROUTE_DEPTHS; depth++) {
    draft.lanes[depth].forEach((lane, i) => {
      const node: RouteNode = {
        id: nodeId(sectorIndex, depth, lane),
        type: draft!.types[depth][i],
        depth,
        lane,
        next: [],
        roomId: '',
        difficulty: depth / (ROUTE_DEPTHS - 1),
        visited: false,
      };
      nodes[node.id] = node;
      ordered.push(node);
    });
  }
  for (let depth = 0; depth < BOSS_DEPTH; depth++) {
    for (const [a, b] of draft.edges[depth]) {
      const from = nodes[nodeId(sectorIndex, depth, draft.lanes[depth][a])];
      const to = nodeId(sectorIndex, depth + 1, draft.lanes[depth + 1][b]);
      if (!from.next.includes(to)) from.next.push(to);
    }
  }
  assignRooms(rng, ordered);

  return {
    sector: sectorIndex,
    nodes,
    startId: nodeId(sectorIndex, 0, 1),
    bossId: nodeId(sectorIndex, BOSS_DEPTH, 1),
    depthCount: ROUTE_DEPTHS,
    lanes: ROUTE_LANES,
  };
}

/** Station depth (exported for UI/tests). */
export const ROUTE_STATION_DEPTH = STATION_DEPTH;
